import React, { useState, useRef, useEffect } from "react";
import "../styles/calculator.css";
import { Link } from "react-router-dom";

// --- Currency & Rate Data ---
const currencies = [
  { code: "AED", symbol: "د.إ" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "INR", symbol: "₹" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
  { code: "JPY", symbol: "¥" },
  { code: "CNY", symbol: "¥" },
  { code: "MXN", symbol: "$" },
  { code: "BRL", symbol: "R$" },
];

const currencyRates = {
  USD: 1,
  EUR: 0.85,
  INR: 83,
  GBP: 0.75,
  AED: 3.67,
  AUD: 1.49,
  CAD: 1.35,
  JPY: 158,
  CNY: 7.25,
  MXN: 18.2,
  BRL: 5.45,
};

// --- Currency Dropdown ---
const CurrencyDropdown = ({ onCurrencyChange }) => {
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSelect = (code, symbol) => {
    setSelectedCurrency(code);
    setCurrencySymbol(symbol);
    setIsOpen(false);
    onCurrencyChange?.({ code, symbol });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="dropdown-wrapper" ref={dropdownRef}>
      <div className="dropdown-selected" onClick={() => setIsOpen(!isOpen)}>
        {selectedCurrency} <span className="symbol">{currencySymbol}</span>
      </div>
      {isOpen && (
        <ul className="dropdown-options">
          {currencies.map((currency) => (
            <li
              key={currency.code}
              className="dropdown-option"
              onClick={() => handleSelect(currency.code, currency.symbol)}
            >
              {currency.code} <span className="symbol">{currency.symbol}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// --- Custom Slider ---
const CustomSlider = ({ setIncome, income }) => {
  const min = 5000,
    max = 500000,
    step = 1000;
  const [value, setValue] = useState(((income - min) / (max - min)) * 100);
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  const normalizeValue = (percent) => min + ((max - min) * percent) / 100;

  const handleStart = (event) => {
    event.preventDefault();
    const slider = sliderRef.current;
    const thumb = thumbRef.current;
    const sliderWidth = slider.offsetWidth;
    document.body.style.userSelect = "none";
    document.body.style.touchAction = "none";

    const getClientX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);

    const onMove = (moveEvent) => {
      let newPercent =
        ((getClientX(moveEvent) - slider.getBoundingClientRect().left) /
          sliderWidth) *
        100;
      newPercent = Math.min(Math.max(newPercent, 0), 100);
      const newValue = normalizeValue(newPercent);
      setValue(newPercent);
      setIncome(Math.round(newValue / step) * step);
      thumb.style.left = `${Math.min(newPercent, 95)}%`;
    };

    const onEnd = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.body.style.userSelect = "";
      document.body.style.touchAction = "";
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
  };

  useEffect(() => {
    const clampedIncome = Math.min(Math.max(income, min), max);
    const percent = ((clampedIncome - min) / (max - min)) * 100;
    setValue(percent);

    if (thumbRef.current) {
      thumbRef.current.style.left = `${Math.min(percent, 95)}%`;
    }
  }, [income]);

  return (
    <div>
      <div
        ref={sliderRef}
        style={{
          position: "relative",
          height: "1px",
          background: "#000",
          borderRadius: "5px",
          width: "100%",
          cursor: "pointer",
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
      >
        <div
          style={{
            position: "absolute",
            height: "1px",
            background: "green",
            borderRadius: "5px",
            width: `${value}%`,
            top: 0,
            left: 0,
          }}
        />
        <div
          ref={thumbRef}
          style={{
            position: "absolute",
            top: "-9px",
            left: `${Math.min(value, 95)}%`,
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            backgroundColor: "#FFF",
            cursor: "pointer",
          }}
        />
      </div>
    </div>
  );
};

// --- Pie Chart ---
const PieChart = ({ values }) => {
  const total = values.reduce((a, b) => a + b.value, 0);

  let cumulative = 0;

  if ((values[2].value === 0) || (values[0].value === 0 && values[1].value === 0)) {
    return (
      <svg
        width={200}
        height={200}
        viewBox="-9 -10 57 57"
        style={{ marginBottom: 18 }}
      >
        {values.map((d, i) => {
          const startAngle = cumulative;
          const angle = 120;
          cumulative += angle;
          const r = 16,
            cx = 20,
            cy = 20;
          const x1 = cx + r * Math.cos(((startAngle - 90) * Math.PI) / 180);
          const y1 = cy + r * Math.sin(((startAngle - 90) * Math.PI) / 180);
          const x2 = cx + r * Math.cos(((cumulative - 90) * Math.PI) / 180);
          const y2 = cy + r * Math.sin(((cumulative - 90) * Math.PI) / 180);
          const largeArc = angle > 180 ? 1 : 0;
          const pathData = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
          const labelAngle = startAngle + angle / 2;
          const labelX =
            cx + (r + 15) * 0.7 * Math.cos(((labelAngle - 90) * Math.PI) / 180);
          const labelY =
            cy + (r + 15) * 0.7 * Math.sin(((labelAngle - 90) * Math.PI) / 180);
          const labelX1 =
            cx + (r + 3) * 0.7 * Math.cos(((labelAngle - 90) * Math.PI) / 180);
          const labelY1 =
            cy + (r + 3) * 0.7 * Math.sin(((labelAngle - 90) * Math.PI) / 180);
  
          return (
            <g key={i}>
              <path d={pathData} fill={"#999"} />
              <circle
                cx={labelX1}
                cy={labelY1}
                r={0.9}
                stroke="#FFF"
                strokeWidth="0.4"
                fill={d.color}
              />
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                fontSize={3}
                fill="#444"
                fontWeight="bold"
              >
                {total === 0 ? "0" : ((100 * d.value) / total).toFixed(2)}
              </text>
            </g>
          );
        })}
        <circle cx={20} cy={20} r={8} fill="#EDEDED" />
      </svg>
    );
  }

  return (
    <svg
      width={200}
      height={200}
      viewBox="-9 -10 57 57"
      style={{ marginBottom: 18 }}
    >
      {values.map((d, i) => {
        const startAngle = cumulative;
        const angle = (d.value / total) * 360;
        cumulative += angle;
        const r = 16,
          cx = 20,
          cy = 20;
        const x1 = cx + r * Math.cos(((startAngle - 90) * Math.PI) / 180);
        const y1 = cy + r * Math.sin(((startAngle - 90) * Math.PI) / 180);
        const x2 = cx + r * Math.cos(((cumulative - 90) * Math.PI) / 180);
        const y2 = cy + r * Math.sin(((cumulative - 90) * Math.PI) / 180);
        const largeArc = angle > 180 ? 1 : 0;
        const pathData = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
        const labelAngle = startAngle + angle / 2;
        const labelX =
          cx + (r + 15) * 0.7 * Math.cos(((labelAngle - 90) * Math.PI) / 180);
        const labelY =
          cy + (r + 15) * 0.7 * Math.sin(((labelAngle - 90) * Math.PI) / 180);
        const labelX1 =
          cx + (r + 3) * 0.7 * Math.cos(((labelAngle - 90) * Math.PI) / 180);
        const labelY1 =
          cy + (r + 3) * 0.7 * Math.sin(((labelAngle - 90) * Math.PI) / 180);

        return (
          <g key={i}>
            <path d={pathData} fill={d.color} />
            <circle
              cx={labelX1}
              cy={labelY1}
              r={0.9}
              stroke="#FFF"
              strokeWidth="0.4"
              fill={d.color}
            />
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              fontSize={3}
              fill="#444"
              fontWeight="bold"
            >
              {total === 0 ? "0" : ((100 * d.value) / total).toFixed(2)}
            </text>
          </g>
        );
      })}
      <circle cx={20} cy={20} r={8} fill="#EDEDED" />
    </svg>
  );
};

// --- Main Calculator Component ---
export default function Calculator() {
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [income, setIncome] = useState("");
  const [years, setYears] = useState("");
  const [liabilities, setLiabilities] = useState("");
  const [goals, setGoals] = useState("");

  const convertInputValues = (input, rate) => {
    return input
      .split(",")
      .map((v) => {
        const n = Number(v.replace(/\$/g, "").replace(/,/g, "").trim());
        return isNaN(n) ? 0 : Math.round(n * rate);
      })
      .join(", ");
  };

  const handleCurrencyChange = ({ code, symbol }) => {
    if (code === currencyCode) return;
    const rate = currencyRates[code] / currencyRates[baseCurrency];
    // setIncome(prev => Math.round(prev * rate));
    // setLiabilities(prev => convertInputValues(prev, rate));
    // setGoals(prev => convertInputValues(prev, rate));
    setCurrencySymbol(symbol);
    setCurrencyCode(code);
    setBaseCurrency(code);
  };

  const liabilitiesTotal =
    liabilities
      .match(/\d+/g) // Extract all number sequences
      ?.map(Number) // Convert them to numbers
      .reduce((a, b) => a + b, 0) || 0; // Sum them, fallback to 0
  const goalsTotal =
    goals
      .match(/\d+/g) // Extract all number sequences
      ?.map(Number) // Convert them to numbers
      .reduce((a, b) => a + b, 0) || 0; // Sum them, fallback to 0
  const incomeReplacement = income * years;
  const totalCover = liabilitiesTotal + goalsTotal + incomeReplacement;

  const pieData = [
    { label: "Liabilities", value: liabilitiesTotal, color: "#42eac1" },
    { label: "Major Goals", value: goalsTotal, color: "#a6fa34" },
    { label: "Income Replacement", value: incomeReplacement, color: "#222" },
  ];

  function formatNumberSmart(num) {
    if (num < 10_000_000) {
      return num.toLocaleString(); // up to 7 digits, show full number
    } else if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    } else if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    } else {
      return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    }
  }

  return (
    <div className="insurance-calc-wrapper">
      <div className="insurance-calc-content">
        <div className="calc-fields">
          <div className="calc-title">Insurance Calculator</div>
          <div className="field-label">Income</div>
          <div className="income-slider-row">
            <div className="income-slider-wrapper">
              <input
                type="text"
                className="income-value-input"
                value={income.toString()}
                onChange={(e) => {
                  const numericValue = Number(
                    e.target.value.replace(/[^0-9]/g, "")
                  );
                  // Enforce max value of 10,000,000,000
                  if (!isNaN(numericValue) && numericValue <= 10_000_000_000) {
                    setIncome(numericValue);
                  }
                }}
                placeholder="10000"
              />
              <CustomSlider setIncome={setIncome} income={income} />
            </div>
            <CurrencyDropdown onCurrencyChange={handleCurrencyChange} />
            <input
              type="number"
              min={1}
              max={60}
              value={years === 0 ? "" : Math.min(years, 60)}
              onChange={(e) => setYears(Number(e.target.value))}
              className="years-input"
              placeholder="7"
            />
          </div>
          <div className="field-desc">
            Yearly Income{" "}
            <span className="field-desc-right">
              No. of years your family needs support
            </span>
          </div>

          <div className="field-label">Liabilities</div>
          <input
            type="text"
            value={liabilities}
            maxLength={500}
            onChange={(e) => {
              const input = e.target.value.replace(/[^\d,\s]/g, ""); // Only digits, commas, spaces

              // Split by comma or space and check for any number > 10B
              const chunks = input.split(/[\s,]+/).filter(Boolean); // filter(Boolean) removes empty strings

              const isValid = chunks.every((num) => {
                const numeric = Number(num);
                return isNaN(numeric) || numeric <= 10_000_000_000;
              });

              if (isValid) {
                setLiabilities(input);
              }
              // else: ignore input that includes any chunk > 10B
            }}
            placeholder="250000, 40000, 3000"
            className="input-liabilities"
          />

          <div className="field-desc">
            Outstanding Loans{" "}
            <span className="field-desc-right">
              Home, Car, Personal Loan, etc.
            </span>
          </div>

          <div className="field-label">Major Goals</div>
          <input
            type="text"
            value={goals}
            maxLength={500}
            onChange={(e) => {
              const input = e.target.value.replace(/[^\d,\s]/g, ""); // Only digits, commas, spaces

              // Split by comma or space and check for any number > 10B
              const chunks = input.split(/[\s,]+/).filter(Boolean); // filter(Boolean) removes empty strings

              const isValid = chunks.every((num) => {
                const numeric = Number(num);
                return isNaN(numeric) || numeric <= 10_000_000_000;
              });

              if (isValid) {
                setGoals(input);
              }
              // else: ignore input that includes any chunk > 10B
            }}
            placeholder="64000, 800000, 100000"
            className="input-liabilities"
          />
          <div className="field-desc">
            Personal Plans{" "}
            <span className="field-desc-right">
              Children’s Education, Marriages, etc.
            </span>
          </div>
        </div>

        <div className="calc-visual">
          <PieChart values={pieData} />
          <div className="legend">
            <div>
              <span className="dot liabilities"></span>Liabilities
            </div>
            <div>
              <span className="dot goals"></span>Major Goals
            </div>
            <div>
              <span className="dot income"></span>Income Replacement
            </div>
          </div>
          <div className="cover-result-label">Minimum Cover Required</div>
          <div className="cover-result-amt">
            {currencySymbol} {formatNumberSmart(totalCover)}
          </div>
        </div>
      </div>

      <div className="insurance-calc-bottom">
        <div>
          <span>Combine life and critical illness cover</span>
          <br />
          <span style={{ fontSize: 14, opacity: 0.75 }}>
            Build a well-rounded risk protection plan
          </span>
        </div>
        <div className="advisor-wrapper">
          <Link to="/contact" className="advisor-btn"
          style={{ textDecoration: "none" }}
          >
            Talk to Advisor
          </Link>
          <span className="for-insights">For more insights</span>
        </div>
      </div>
    </div>
  );
}
